import { useState } from 'preact/hooks';
import type { ParsedCert } from '../../core/x509/parse';
import { certFingerprint } from '../../core/x509/parse';
import { useAsync } from '../hooks/useAsync';
import { Badge } from './Badge';
import { Hash } from './Hash';
import { TimeStamp } from './TimeStamp';

export function CertSummary({ cert }: { cert: ParsedCert }) {
  const fp = useAsync(() => certFingerprint(cert.der), [cert.der]);
  const [allSans, setAllSans] = useState(false);
  const now = Date.now();
  const expired = cert.notAfter.getTime() < now;
  const notYet = cert.notBefore.getTime() > now;
  const sans = allSans ? cert.sans : cert.sans.slice(0, 6);
  return (
    <dl class="kv">
      <dt>Subject</dt>
      <dd class="mono wrap">{cert.subject || <span class="muted">(empty)</span>}</dd>
      {cert.sans.length > 0 && (
        <>
          <dt>Alt names</dt>
          <dd>
            <div class="row" style="gap:0.3rem">
              {sans.map((s) => <span key={s} class="chip"><code>{s}</code></span>)}
              {cert.sans.length > 6 && (
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => setAllSans((v) => !v)}>{allSans ? 'Show fewer' : `+${cert.sans.length - 6} more`}</button>
              )}
            </div>
          </dd>
        </>
      )}
      <dt>Issuer</dt>
      <dd class="mono wrap">{cert.issuer}</dd>
      <dt>Serial</dt>
      <dd><Hash value={cert.serialHex} /></dd>
      <dt>Validity</dt>
      <dd class="row">
        <span><TimeStamp ms={cert.notBefore} relative={false} /> → <TimeStamp ms={cert.notAfter} relative={false} /></span>
        {expired ? <Badge tone="muted">expired</Badge> : notYet ? <Badge tone="warn">not yet valid</Badge> : <Badge tone="ok">valid now</Badge>}
      </dd>
      <dt>Public key</dt>
      <dd>{cert.publicKeyAlgorithm}</dd>
      <dt>Signature</dt>
      <dd class="row">
        {cert.signatureAlgorithm}
        {cert.isTbsOnly && <Badge tone="outline" title="Log entries carry only the TBSCertificate of a precertificate; there is no signature to check.">TBS only</Badge>}
      </dd>
      <dt>SHA-256</dt>
      <dd><Hash value={fp.value} /></dd>
      <dt>Flags</dt>
      <dd class="row">
        {cert.isCA && <Badge tone="info">CA</Badge>}
        {cert.hasPoison && <Badge tone="warn" title="Contains the CT poison extension (1.3.6.1.4.1.11129.2.4.3): this is a precertificate">precertificate (poison)</Badge>}
        {cert.isPrecertSigningCert && <Badge tone="info">precert signing certificate</Badge>}
        {cert.scts.length > 0 && <Badge tone="ok">{cert.scts.length} embedded SCT{cert.scts.length === 1 ? '' : 's'}</Badge>}
        {!cert.isCA && !cert.hasPoison && !cert.isPrecertSigningCert && cert.scts.length === 0 && <span class="muted">—</span>}
      </dd>
    </dl>
  );
}
